import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TalentResult, TalentType, talentResultsEn, talentResultsZh } from './TalentTestData';
import { useLang } from '../../../i18n/LanguageContext';

interface TalentTestResultProps {
  scores: Record<TalentType, number>;
  onRetake: () => void;
  isSaved?: boolean;
  currentUser?: { name: string; email: string } | null;
}

const ORDER: TalentType[] = ['S', 'J', 'P', 'N'];

const TYPE_META_EN: Record<TalentType, { label: string; color: string; icon: string }> = {
  S: { label: 'Seer', color: '#a78bfa', icon: 'ri-eye-2-line' },
  J: { label: 'Interpreter', color: '#f4d03f', icon: 'ri-book-open-line' },
  P: { label: 'Practitioner', color: '#34d399', icon: 'ri-flask-line' },
  N: { label: 'Healer', color: '#f9a8d4', icon: 'ri-heart-2-line' },
};

const TYPE_META_ZH: Record<TalentType, { label: string; color: string; icon: string }> = {
  S: { label: '感知者', color: '#a78bfa', icon: 'ri-eye-2-line' },
  J: { label: '解读者', color: '#f4d03f', icon: 'ri-book-open-line' },
  P: { label: '实践者', color: '#34d399', icon: 'ri-flask-line' },
  N: { label: '疗愈者', color: '#f9a8d4', icon: 'ri-heart-2-line' },
};

export default function TalentTestResult({ scores, onRetake, isSaved, currentUser }: TalentTestResultProps) {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [revealed, setRevealed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [barsAnimated, setBarsAnimated] = useState(false);
  const [activeTab, setActiveTab] = useState<'traits' | 'signs'>('traits');

  const talentResults = lang === 'zh' ? talentResultsZh : talentResultsEn;
  const TYPE_META = lang === 'zh' ? TYPE_META_ZH : TYPE_META_EN;

  const primary = ORDER.reduce((a, b) => (scores[a] >= scores[b] ? a : b));
  const result: TalentResult = talentResults[primary];
  const total = Math.max(Object.values(scores).reduce((a, b) => a + b, 0), 1);

  useEffect(() => {
    setRevealed(false);
    setShowDetails(false);
    setBarsAnimated(false);
    const t1 = setTimeout(() => setRevealed(true), 100);
    const t2 = setTimeout(() => setShowDetails(true), 1100);
    const t3 = setTimeout(() => setBarsAnimated(true), 1400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [scores]);

  const pct = (type: TalentType) => Math.round((scores[type] / total) * 100);
  const sortedTypes = [...ORDER].sort((a, b) => scores[b] - scores[a]);

  return (
    <div className="space-y-8">

      {/* 历史结果提示横幅 */}
      {isSaved && currentUser && (
        <div
          className="flex items-center justify-between px-5 py-3 rounded-xl border"
          style={{ background: 'rgba(212,175,55,0.08)', borderColor: 'rgba(212,175,55,0.25)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#d4af37] to-[#f4d03f] flex items-center justify-center text-[#1a0a2e] font-bold text-sm flex-shrink-0">
              {currentUser.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-[#d4af37] text-sm font-medium">
                {t(`${currentUser.name}'s Last Result`, `${currentUser.name} 的上次测试结果`)}
              </p>
              <p className="text-[#c8b6ff]/50 text-xs">{t('Auto-saved · Retake anytime', '已自动保存 · 随时重新测试')}</p>
            </div>
          </div>
          <button
            onClick={onRetake}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-[#d4af37]/40 text-[#d4af37] text-xs hover:bg-[#d4af37]/15 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-xs"></i>
            <span>{t('Retake', '重新测试')}</span>
          </button>
        </div>
      )}

      {/* 主揭晓卡 */}
      <div className={`transition-all duration-1000 ${revealed ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`}>
        <div
          className="relative rounded-3xl overflow-hidden border border-white/10 p-8 lg:p-12 text-center"
          style={{
            background: `radial-gradient(ellipse at 30% 0%, ${result.gradientFrom}90 0%, transparent 55%), radial-gradient(ellipse at 70% 100%, ${result.gradientTo}60 0%, transparent 55%), #0d0221`,
          }}
        >
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 24 }).map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full animate-pulse"
                style={{
                  width: (i % 3 + 1) * 2 + 'px',
                  height: (i % 3 + 1) * 2 + 'px',
                  top: (i * 17 + 7) % 100 + '%',
                  left: (i * 23 + 5) % 100 + '%',
                  background: result.color,
                  opacity: 0.08 + (i % 4) * 0.06,
                  animationDuration: (i % 3 + 2) + 's',
                  animationDelay: (i % 5) * 0.5 + 's',
                }}
              />
            ))}
          </div>

          <div className="relative z-10 mb-6">
            <div className="relative w-28 h-28 mx-auto">
              <div className="absolute inset-0 rounded-full animate-pulse" style={{ background: `radial-gradient(circle, ${result.color}25, transparent)` }} />
              <div
                className="w-full h-full rounded-full flex items-center justify-center"
                style={{ background: `radial-gradient(circle at 40% 35%, ${result.color}30, ${result.gradientTo}50)`, border: `2px solid ${result.color}50` }}
              >
                <i className={`${result.icon} text-5xl`} style={{ color: result.color }}></i>
              </div>
            </div>
          </div>

          <div className="relative z-10 space-y-3 mb-8">
            <p className="text-xs tracking-[0.35em] uppercase font-medium" style={{ color: result.color }}>
              {t('Your Spiritual Gift Type', '你的灵性天赋类型')}
            </p>
            <h1 className="text-4xl lg:text-5xl font-serif text-white">{result.name}</h1>
            <p className="text-base font-light tracking-wide" style={{ color: result.color }}>{result.subtitle}</p>
            <div className="w-16 h-px mx-auto" style={{ background: `linear-gradient(90deg, transparent, ${result.color}, transparent)` }} />
            <p className="text-[#c8b6ff] text-base max-w-lg mx-auto leading-relaxed italic">&ldquo;{result.tagline}&rdquo;</p>
          </div>

          <div className="relative z-10 space-y-3 max-w-sm mx-auto">
            {sortedTypes.map((tp) => {
              const meta = TYPE_META[tp];
              const p = pct(tp);
              const isPrimary = tp === primary;
              return (
                <div key={tp} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${meta.icon} text-sm`} style={{ color: meta.color }}></i>
                  </div>
                  <span className="text-xs w-20 text-right flex-shrink-0" style={{ color: meta.color }}>{meta.label}</span>
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-1000 ease-out"
                      style={{
                        width: barsAnimated ? `${p}%` : '0%',
                        background: isPrimary ? `linear-gradient(90deg, ${meta.color}cc, ${meta.color})` : `${meta.color}60`,
                        transitionDelay: sortedTypes.indexOf(tp) * 120 + 'ms',
                      }}
                    />
                  </div>
                  <span className="text-xs w-8 flex-shrink-0 font-semibold" style={{ color: isPrimary ? meta.color : 'rgba(200,182,255,0.5)' }}>
                    {p}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 详情区 */}
      <div className={`transition-all duration-700 ${showDetails ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

        <div className="rounded-2xl p-6 mb-6 border" style={{ background: `${result.gradientFrom}18`, borderColor: `${result.color}25` }}>
          <p className="text-[#e8e0ff] leading-relaxed text-sm lg:text-base">{result.description}</p>
        </div>

        {/* Tab */}
        <div className="mb-5">
          <div className="inline-flex rounded-full p-1 gap-0.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {(['traits', 'signs'] as const).map((tab) => {
              const labels = {
                traits: t('Gift Traits', '天赋特质'),
                signs: t('Gift Signs', '天赋印记'),
              };
              const icons = { traits: 'ri-sparkling-line', signs: 'ri-footprint-line' };
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                  style={activeTab === tab ? { background: result.color, color: '#0d0221' } : { color: 'rgba(200,182,255,0.6)' }}
                >
                  <i className={`${icons[tab]} text-xs`}></i>
                  <span>{labels[tab]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {activeTab === 'traits' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {result.traits.map((trait, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl border" style={{ background: `${result.color}06`, borderColor: `${result.color}18` }}>
                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg" style={{ background: `${result.color}18` }}>
                  <i className="ri-check-line text-sm" style={{ color: result.color }}></i>
                </div>
                <span className="text-white text-sm font-medium">{trait}</span>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'signs' && (
          <div className="space-y-3 mb-8">
            {result.giftSigns.map((sign, i) => (
              <div key={i} className="flex items-start gap-4 p-4 rounded-xl border" style={{ background: `${result.color}06`, borderColor: `${result.color}18` }}>
                <div
                  className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full mt-0.5 text-xs font-bold"
                  style={{ background: `${result.color}20`, color: result.color }}
                >
                  {i + 1}
                </div>
                <p className="text-[#e8e0ff] text-sm leading-relaxed">{sign}</p>
              </div>
            ))}
          </div>
        )}

        {/* 推荐课程 */}
        <div className="mt-2">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${result.color}40, transparent)` }} />
            <div className="flex items-center gap-2">
              <i className="ri-graduation-cap-line text-sm" style={{ color: result.color }}></i>
              <span className="text-sm font-semibold" style={{ color: result.color }}>
                {t('Courses Matched to Your Gift', '为你精准匹配的课程')}
              </span>
            </div>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${result.color}40)` }} />
          </div>

          <p className="text-[#c8b6ff]/55 text-xs mb-5 text-center">
            {t('Precisely matched to your gift type — click to view details', '根据你的天赋类型精准匹配——点击查看课程详情')}
          </p>

          <div className="space-y-4">
            {result.courses.map((course, i) => (
              <div
                key={i}
                className="group relative overflow-hidden rounded-2xl border transition-all duration-300 hover:scale-[1.01] cursor-pointer"
                style={{ background: `${result.gradientFrom}20`, borderColor: `${result.color}25` }}
                onClick={() => navigate(`/course/${course.courseId}`)}
              >
                <div
                  className="absolute top-0 right-0 px-3 py-1 rounded-bl-xl text-xs font-semibold"
                  style={{ background: `${result.color}22`, color: result.color }}
                >
                  {course.tag}
                </div>
                <div className="flex items-start gap-4 p-5 pr-20">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${result.color}18` }}>
                    <i className={`${course.icon} text-xl`} style={{ color: result.color }}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold text-sm mb-1.5">{course.title}</h3>
                    <p className="text-[#c8b6ff]/70 text-xs leading-relaxed">{course.desc}</p>
                  </div>
                </div>
                <div
                  className="flex items-center justify-between px-5 py-2.5 border-t"
                  style={{ borderColor: `${result.color}15`, background: `${result.color}06` }}
                >
                  <span className="text-xs" style={{ color: `${result.color}90` }}>
                    {t('View Course', '查看课程详情')}
                  </span>
                  <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform" style={{ color: result.color }}></i>
                </div>
              </div>
            ))}

            <button
              onClick={() => navigate('/courses')}
              className="w-full py-4 mt-1 rounded-2xl font-semibold text-sm transition-all duration-300 hover:opacity-90 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              style={{ background: `linear-gradient(135deg, ${result.gradientFrom}, ${result.gradientTo})`, color: 'white', border: `1px solid ${result.color}35` }}
            >
              <i className="ri-apps-line"></i>
              <span>{t('Browse All Courses', '查看全部课程')}</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>

      {!isSaved && (
        <div className={`flex flex-col sm:flex-row gap-3 pt-2 transition-all duration-700 delay-300 ${showDetails ? 'opacity-100' : 'opacity-0'}`}>
          <button
            onClick={onRetake}
            className="flex-1 py-3.5 rounded-xl border border-white/15 text-[#c8b6ff]/70 hover:border-white/30 hover:text-white transition-all duration-300 cursor-pointer text-sm font-medium flex items-center justify-center gap-2"
          >
            <i className="ri-refresh-line"></i>
            <span>{t('Retake Test', '重新测试')}</span>
          </button>
          <button
            onClick={() => navigate('/courses')}
            className="flex-1 py-3.5 rounded-xl text-white font-semibold text-sm transition-all duration-300 hover:opacity-90 cursor-pointer flex items-center justify-center gap-2"
            style={{ background: `linear-gradient(135deg, ${result.gradientFrom}, ${result.gradientTo})` }}
          >
            <i className="ri-graduation-cap-line"></i>
            <span>{t('Explore My Courses', '探索适合我的课程')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
